import { GridRowId } from '@mui/x-data-grid';
import { useMutation, useQueryClient } from 'react-query';
import { useApiGet, useApiPost } from 'src/api';
import { useBuildVars } from 'src/config';
import { LineDetails } from 'src/views/vendorprogram/hooks/deal-details';

export const initialPageSetup = { page: 1, pageSize: 100 };

export type DealDetails = {
  dealId: number;
  dealNumber: string;
  discountType?: string;
  importreferenceid: number;
  lines: LineDetails[];
  vendorId: number;
  vendorProgramId: number;
};

export const getFlattenedLines = (rawLines: LineDetails[] = []) =>
  rawLines.flatMap((line) => [
    line,
    ...(line?.childDiscountLines ? line.childDiscountLines.map((c) => c) : []),
  ]);

export function addChildItems(
  selectedLines: Record<GridRowId, number>,
  rawLines?: LineDetails[]
) {
  const updatedLines: Record<GridRowId, number> = {};

  getFlattenedLines(rawLines).forEach((item) => {
    if (selectedLines[item.item_id] !== undefined) {
      updatedLines[item.item_id] = selectedLines[item.item_id];
      if (item.childDiscountLines?.length && item.productset_type === 'BL') {
        item.childDiscountLines.forEach((child) => {
          updatedLines[child.item_id] =
            (child.dd_max_qty_limit / item.dd_vendor_rem_max_qty_limit) *
            selectedLines[item.item_id];
        });
      }
    }
  });

  return updatedLines;
}

export function useGenerateQuote({
  dealId: bidId,
  dealNumber: bidNumber,
  discountType,
  lines,
  vendorId,
  vendorProgramId,
}: Partial<DealDetails>) {
  const buildVars = useBuildVars();
  const apiPost = useApiPost({
    host: buildVars.VP_API_BASE,
  });
  return useMutation(
    (props: {
      bcnList: string | null;
      bidItemList: Record<number, number>;
      isFullQuote: boolean;
      resellerName: string | null;
    }) =>
      apiPost('/system.webapi.gds/api/v1/GdsCoreApiQuoteCreation/CreateQuote', {
        bcnList: props.bcnList,
        bidNumber,
        discountType,
        quoteDetailsDto: {
          isFullQuote: props.isFullQuote,
          isManual: true,
          skSbo: String(bidId),
          ...(Object.keys(props.bidItemList).length && {
            bidItems: Object.entries(
              addChildItems(props.bidItemList, lines)
            ).map(([itemId, quantity]) => ({
              itemId: Number(itemId),
              quantity,
            })),
          }),
        },
        resellerName: props.resellerName,
        vendorId,
        vendorProgramId,
      })
  );
}

export interface RefreshSKU {
  company: string;
  isXcmVendor: boolean;
  lineItems: {
    trackingNumber: string;
    vendorCode: string;
    vpn: string;
  }[];
}

export interface RefreshSKUForRulesEngine {
  bidNumber: string;
  productLines: {
    additionalAttributes: {
      attributename: string;
      attributevalue: string;
    }[];
    discountDetailId: number;
    discountTrackingId: number;
    productBusinessType: string;
    productSetType: string;
    sbo2ProductSetId: string;
    vpn: string;
  }[];
  productLocalizationForWeb: string;
  skSbo: string | number;
  vendor: string;
  vendorProgramId: string;
  vendorProgramName: string;
}
export function useRefreshImSku(
  reqBody: RefreshSKU,
  importreferenceid: number
) {
  const buildVars = useBuildVars();
  const queryClient = useQueryClient();
  const apiPost = useApiPost({ host: buildVars.VP_API_BASE });
  return useMutation(
    () =>
      apiPost(
        '/system.webapi.gds/api/v1/GdsCoreApiExceptionBids/RefreshBlankSKUAsync',
        reqBody
      ),
    {
      onSuccess: () =>
        queryClient.invalidateQueries(
          `/system.webapi.gds/api/v1/GdsCoreApiDeals/${importreferenceid}`
        ),
    }
  );
}

export function useRefreshImSkuForRulesEngine(
  importreferenceid: number,
  reqBody: RefreshSKUForRulesEngine
) {
  const buildVars = useBuildVars();
  const queryClient = useQueryClient();
  const apiPost = useApiPost({ host: buildVars.VP_API_BASE });
  return useMutation(
    () =>
      apiPost(
        '/system.webapi.gds/api/v1/GdsCoreApiExceptionBids/RefreshBlankSKUFromRulesEngine',
        reqBody
      ),
    {
      onSuccess: () =>
        queryClient.invalidateQueries(
          `/system.webapi.gds/api/v1/GdsCoreApiDeals/${importreferenceid}`
        ),
    }
  );
}

type GetQuoteNumberRequest = {
  BidNumber: string;
  DealId: string;
  DiscountType: string;
  ResellerNumber: string;
  VendorProgramId: number;
};
export function useGetQuoteNumber(params: GetQuoteNumberRequest) {
  const buildVars = useBuildVars();
  const apiGet = useApiGet<string>({ host: buildVars.VP_API_BASE });
  return useMutation(() =>
    apiGet(
      '/system.webapi.gds/api/v1/GdsCoreApiQuoteCreation/GetQuoteNumber',
      params
    )
  );
}
